import { ResourceHeader } from "@/components/ResourceHeader";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/auth-context";
import {
  useActiveOrganization,
  useActiveOrgWorkspaces,
} from "@/hooks/organizations";
import { useIsWorkspaceAdmin } from "@/hooks/workspaces";
import { useDeleteWorkspace } from "@/queries";
import {
  useActiveOrgMembers,
  useDeleteOrgMember,
} from "@/queries/organization-members";
import { useEditOrganization } from "@/queries/organizations";
import {
  useCreateWorkspaceInvite,
  WorkspaceInviteIn,
} from "@/queries/workspace-invites";
import { OrganizationMember } from "@/types/organization-member";
import { Workspace } from "@/types/workspaces";
import { CheckIcon, ChevronsUpDown, TrashIcon } from "lucide-react";
import { useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import ArchiveConfirmationForm from "./DatasetEditor/Header/ArchiveButton/ArchiveConfirmationForm";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "./ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const Organization = () => {
  return (
    <div className="mt-4 flex flex-col gap-4">
      <OrganizationName />
      <Workspaces />
      <OrganizationMembers />
    </div>
  );
};

const OrganizationName = () => {
  const authContext = useAuth();
  const userToken = authContext!.userToken;
  const currentOrganization = useActiveOrganization()!;
  const { mutateAsync: editOrganization } = useEditOrganization(userToken!);

  const handleEditOrganizationName = ({
    onSuccess,
    onError,
    newTitle,
  }: {
    onSuccess?: (newTitle: string) => void;
    onError?: () => void;
    newTitle: string;
  }) => {
    editOrganization(
      {
        organizationId: currentOrganization.id,
        name: newTitle,
      },
      {
        onSuccess: (data) => {
          if (onSuccess) {
            onSuccess(data.name);
          }
        },
        onError: () => {
          if (onError) {
            onError();
          }
        },
      },
    );
  };
  return (
    <ResourceHeader
      id={currentOrganization.id}
      title={currentOrganization.name}
      onTitleEdit={handleEditOrganizationName}
      createdAt={currentOrganization.created_at}
      minimal={true}
    />
  );
};

const Workspaces = () => {
  const workspaces = useActiveOrgWorkspaces();
  return (
    <div className="mt-2 px-2">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline">
          <h1 className="text-lg font-semibold">Workspaces</h1>
        </div>
      </div>
      <div className="mt-2 w-full border-t border-gray-200 pt-4">
        <ul className="space-y-2">
          {workspaces.map((workspace) => (
            <li
              className="flex items-center justify-between rounded-md bg-gray-50 p-2 hover:bg-gray-100"
              key={workspace.id}
            >
              <div className="flex items-center space-x-2">
                <span className="text-gray-700">{workspace.name}</span>
                {workspace.is_admin && (
                  <span className="inline-block rounded-full bg-blue-500 px-2 text-xs text-white">
                    Admin
                  </span>
                )}
              </div>
              <DeleteWorkspace workspace={workspace} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const DeleteWorkspace = ({ workspace }: { workspace: Workspace }) => {
  const [open, setOpen] = useState(false);
  const deleteWorkspace = useDeleteWorkspace();
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const onSuccess = () => {
    deleteWorkspace.mutate(workspace.id);
  };
  if (workspace.is_default) return null;
  if (!isWorkspaceAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="inline-flex items-center"
          size="sm"
          variant="destructiveOutline"
        >
          <TrashIcon className="h-4 w-auto pr-1" aria-hidden="true" />
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent>
        <ArchiveConfirmationForm
          name={workspace.name}
          setOpen={setOpen}
          onSuccess={onSuccess}
        />
      </DialogContent>
    </Dialog>
  );
};

const OrganizationMembers = () => {
  const usersQuery = useActiveOrgMembers();
  const members = usersQuery.data?.items || [];
  return (
    <div className="mt-2 px-2">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline">
          <h1 className="text-lg font-semibold">Members</h1>
        </div>
        <InviteMember />
      </div>
      <div className="mt-2 w-full border-t border-gray-200 pt-4">
        <ul className="space-y-2">
          {members.map((member) => (
            <li
              className="flex items-center justify-between rounded-md bg-gray-50 p-2 hover:bg-gray-100"
              key={member.id}
            >
              <div className="flex items-center space-x-2">
                <span className="text-gray-700">{member.user.email}</span>
                {member.is_owner ? <Badge>Owner</Badge> : null}
              </div>
              <DeleteMember organizationMember={member} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const DeleteMember = ({
  organizationMember,
}: {
  organizationMember: OrganizationMember;
}) => {
  const deleteOrganizationMember = useDeleteOrgMember();
  const handleClick = () => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this member? This action will remove them from the organization and all workspaces of the organization. This action cannot be undone.",
    );
    if (isConfirmed) deleteOrganizationMember.mutate(organizationMember.id);
  };
  if (organizationMember.is_owner) return null;
  return (
    <Button
      type="button"
      className="inline-flex items-center"
      size="sm"
      variant="destructiveOutline"
      onClick={handleClick}
    >
      <TrashIcon className="h-4 w-auto pr-1" aria-hidden="true" />
      Delete
    </Button>
  );
};

const InviteMember = () => {
  return (
    <Dialog>
      <DialogTrigger>
        <Button size="sm">Invite</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite people</DialogTitle>
        </DialogHeader>
        <CreateInviteForm />
      </DialogContent>
    </Dialog>
  );
};

const CreateInviteForm = () => {
  const form = useForm<WorkspaceInviteIn>();

  return (
    <Form {...form}>
      <SelectWorkspace />
      <SelectEmails />
      <Submit />
    </Form>
  );
};

const SelectWorkspace = () => {
  const form = useFormContext<WorkspaceInviteIn>();
  const workspaces = useActiveOrgWorkspaces();
  return (
    <FormField
      control={form.control}
      name="workspace_id"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Workspace</FormLabel>
          <FormControl>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value?.toString()}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((workspace) => (
                  <SelectItem
                    key={workspace.id}
                    value={workspace.id.toString()}
                  >
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
        </FormItem>
      )}
    />
  );
};

const SelectEmails = () => {
  const form = useFormContext<WorkspaceInviteIn>();
  const emails = Array.from(form.watch("emails") ?? []);
  const orgMembersQuery = useActiveOrgMembers();
  const members = orgMembersQuery.data?.items || [];
  const [newEmail, setNewEmail] = useState("");
  return (
    <FormField
      control={form.control}
      name="emails"
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>Emails</FormLabel>
          <FormControl>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="justify-between"
                >
                  {emails.length > 0
                    ? emails.map((email) => email).join(", ")
                    : "Select emails"}
                  <ChevronsUpDown className="w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search framework..."
                    value={newEmail}
                    onValueChange={setNewEmail}
                  />
                  <CommandList>
                    <CommandEmpty>No member found.</CommandEmpty>
                    <CommandGroup>
                      {members.map((member) => (
                        <CommandItem
                          key={member.user_id}
                          value={member.user.email}
                          onSelect={(currentValue) => {
                            field.onChange(
                              emails.some((email) => email === currentValue)
                                ? emails.filter(
                                    (email) => email !== currentValue,
                                  )
                                : [...emails, currentValue],
                            );
                          }}
                        >
                          <div
                            className="size-4 *:[svg]:opacity-0 data-[selected=true]:*:[svg]:opacity-100 pointer-events-none shrink-0 select-none rounded-[4px] border border-input transition-all data-[selected=true]:border-primary data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground"
                            data-selected={emails.some(
                              (email) => email === member.user.email,
                            )}
                          >
                            <CheckIcon className="size-3.5 text-current" />
                          </div>
                          <div className="ml-1.5">{member.user.email}</div>
                        </CommandItem>
                      ))}
                      {newEmail ? (
                        <CommandItem
                          className="cursor-default select-none"
                          onSelect={() => {
                            field.onChange([...emails, newEmail]);
                            setNewEmail("");
                          }}
                        >
                          <div className="size-4 *:[svg]:opacity-0 data-[selected=true]:*:[svg]:opacity-100 pointer-events-none shrink-0 select-none rounded-[4px] border border-input transition-all data-[selected=true]:border-primary data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground">
                            <CheckIcon className="size-3.5 text-current" />
                          </div>
                          <div className="ml-1.5">{newEmail}</div>
                        </CommandItem>
                      ) : null}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </FormControl>
        </FormItem>
      )}
    />
  );
};

const Submit = () => {
  const createWorkspaceInvite = useCreateWorkspaceInvite();

  const form = useFormContext<WorkspaceInviteIn>();
  const count = form.watch("emails")?.length ?? 0;
  let invite = "Invite";
  if (count > 1) {
    invite = `${invite} ${count} people`;
  } else if (count > 0) {
    invite = `${invite} ${count} person`;
  }

  const handleClick = form.handleSubmit((data: WorkspaceInviteIn) => {
    createWorkspaceInvite.mutate(data);
    form.reset();
  });

  return (
    <DialogFooter>
      <DialogClose>
        <Button variant="secondary">Cancel</Button>
      </DialogClose>
      <Button onClick={handleClick} disabled={!form.formState.isValid}>
        {invite}
      </Button>
    </DialogFooter>
  );
};

export default Organization;
